import { IMPORT_PROPERTY } from './serializedEachImportConstants';

/**
 * Serialized Each — resolve the item-report `routePath` + Import search-element id from metadata.
 *
 * A personal `application:savedFilter` is a client construct: it needs a `routePath` that matches the
 * report page's route (Personal Filters are scoped `byCurrentRoute`) and a `filters` map keyed by the
 * report's search-element hNode ids (WORKING-DOC §1). Neither is a constant:
 *   - The route is `/g/{group}/Reports/{PluralizedItemLabel}`, built at runtime by concatenating nav
 *     heading titles (the tenant-customizable, already-server-resolved captions). So we resolve it by
 *     walking the nav tree and accumulating `navHeading` titles — never by string-building a suffix.
 *   - The element id is the hNode id of a hidden `meta.import` search element authored into the report
 *     page. Metadata is user-editable, so we look it up rather than hardcode it.
 *
 * The hidden Import search element is the single source of truth: its presence marks the report page,
 * its ancestor nav-heading titles form the route, and its id is the `filters` key. Until that element
 * is authored into the report metadata this returns `null`, and the caller no-ops safely.
 *
 * @param {Object} deps
 * @param {Object} deps.metadata the `lib_ui-services` metadata service (`getRawMetadata`, `findHNode`).
 * @param {Function} deps.getRouter the `lib_ui-services` getRouter accessor (`getRoot()` → `/g/{group}/`).
 * @returns {{ routePath: string, importElementId: string } | null}
 */
export default function resolveItemReportFilterTarget({ metadata, getRouter }) {
    const rootHNodes = metadata?.getRawMetadata?.()?.hNodes;
    if (!rootHNodes) return null;

    let root;
    try {
        root = getRouter().getRoot(); // e.g. '/g/0/'
    } catch {
        // Router not yet initialized — nothing we can resolve.
        return null;
    }

    // A search element is our Import element if it (or a descendant, e.g. the `PropertiesToSearch`
    // child) filters on `meta.import`.
    const isImportSearchElement = node =>
        node?.hNodeTypeGroup === 'searchElement' &&
        !!metadata.findHNode(
            node,
            n => Array.isArray(n.propertiesToSearch) && n.propertiesToSearch.includes(IMPORT_PROPERTY)
        );

    let found = null;
    const walk = (node, route) => {
        if (!node || found) return;
        // Only `navHeading` hNodes contribute route segments (matches RouteContainer's route builder).
        let nextRoute = route;
        if (node.hNodeTypeGroup === 'navHeading' && node.hNodeType !== 'NavHeadingRecordList') {
            nextRoute = normalizedNewRoute(route, node.title);
        }
        if (isImportSearchElement(node)) {
            found = { routePath: nextRoute, importElementId: node.id };
            return;
        }
        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                walk(child, nextRoute);
                if (found) return;
            }
        }
    };

    const roots = Array.isArray(rootHNodes) ? rootHNodes : [rootHNodes];
    for (const r of roots) {
        walk(r, root);
        if (found) break;
    }
    return found;
}

// Mirrors RouteContainer's `normalizedNewRoute` (not exported there); trivial join that tolerates a
// trailing slash on the root segment.
function normalizedNewRoute(currentRoute, newAddition) {
    return currentRoute.endsWith('/') ? currentRoute + newAddition : `${currentRoute}/${newAddition}`;
}
